import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { AdminGrant, AdminUser } from "@equipped/core/backend/client";
import { CatalogNode } from "@equipped/core/content/catalogTree";
import { catalogChildTargets, GrantTarget, grantTargets, rootCatalogNodes } from "@equipped/core/content/grantTargets";
import { useAuth } from "../auth/AuthContext";
import { useCatalogTree } from "../lib/useCatalogTree";
import { publicContentBaseUrl } from "../lib/contentBase";
import { userMatchesQuery } from "../lib/userSearch";
import { MainCard } from "../components/MainCard";
import { TabbedPage } from "../components/TabbedPage";

const MAX_SEARCH_RESULTS = 60;

/**
 * The "who has access" roster machinery — shared verbatim between the Browse tab's tree rows
 * and the Search tab's flat rows so the two views can't drift on add/revoke behavior. Lifted
 * out of the page component (docs/365-ADMIN-USER-COMPLETION-AND-CATALOGS-TABS.md item 2/3)
 * unchanged in substance from the original single-list page — only the presentation is now
 * shared by two callers instead of hardcoded into one `.map()`.
 */
function useAccessRoster(client: ReturnType<typeof useAuth>["client"], token: string | null) {
  const [open, setOpen] = useState<string | null>(null);
  const [rosters, setRosters] = useState<Record<string, AdminGrant[]>>({});
  const [rosterBusy, setRosterBusy] = useState<string | null>(null);
  const [rosterError, setRosterError] = useState<string | null>(null);
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [addQuery, setAddQuery] = useState("");

  const usersQuery = useQuery({
    queryKey: ["admin-users", false],
    queryFn: () => client!.adminUsers(token!),
    enabled: !!client && !!token,
  });
  const users = usersQuery.data ?? [];
  const emailFor = useMemo(() => {
    const m = new Map(users.map((u) => [u.id, u.email]));
    return (userId: string) => m.get(userId) ?? userId;
  }, [users]);

  const loadRoster = async (key: string, kind: "pack" | "catalog", ref: string) => {
    setRosterBusy(key);
    setRosterError(null);
    try {
      const list = await client!.adminGrantsByRef(token!, kind, ref);
      setRosters((r) => ({ ...r, [key]: list }));
    } catch (e) {
      setRosterError((e as Error).message);
    } finally {
      setRosterBusy(null);
    }
  };

  const toggle = (kind: "pack" | "catalog", ref: string) => {
    const key = `${kind}:${ref}`;
    setAddPersonOpen(false);
    setAddQuery("");
    if (open === key) {
      setOpen(null);
      return;
    }
    setOpen(key);
    if (!rosters[key]) void loadRoster(key, kind, ref);
  };

  const revoke = async (kind: "pack" | "catalog", ref: string, userId: string) => {
    const key = `${kind}:${ref}`;
    setRosterBusy(key);
    setRosterError(null);
    try {
      await client!.adminRevokeGrant(token!, userId, kind, ref);
      await loadRoster(key, kind, ref);
    } catch (e) {
      setRosterError((e as Error).message);
      setRosterBusy(null);
    }
  };

  const addPerson = async (kind: "pack" | "catalog", ref: string, userId: string) => {
    const key = `${kind}:${ref}`;
    setRosterBusy(key);
    setRosterError(null);
    try {
      await client!.adminAddGrant(token!, userId, kind, ref);
      await loadRoster(key, kind, ref);
      setAddQuery("");
    } catch (e) {
      setRosterError((e as Error).message);
      setRosterBusy(null);
    }
  };

  return {
    users,
    usersQuery,
    emailFor,
    open,
    rosters,
    rosterBusy,
    rosterError,
    addPersonOpen,
    addQuery,
    setAddQuery,
    setAddPersonOpen,
    toggle,
    revoke,
    addPerson,
  };
}

type AccessRoster = ReturnType<typeof useAccessRoster>;

/** The expand-on-click "who has access" body — add/revoke chips + the add-person search box.
 * Reused by both the flat search row and the tree browse row. */
function AccessRosterPanel({ roster, target }: { roster: AccessRoster; target: GrantTarget }) {
  const key = `${target.kind}:${target.ref}`;
  const list = roster.rosters[key];
  const busy = roster.rosterBusy === key;
  const filteredUsers = roster.users
    .filter((u: AdminUser) => userMatchesQuery(u, roster.addQuery))
    .filter((u: AdminUser) => !(list ?? []).some((g) => g.userId === u.id))
    .slice(0, 20);

  return (
    <Box sx={{ mx: 2, mb: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
      {busy && !list ? (
        <CircularProgress size={18} />
      ) : (list?.length ?? 0) === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No one has access yet.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
          {(list ?? []).map((g) => (
            <Chip
              key={g.userId}
              label={`${roster.emailFor(g.userId)} ✕`}
              size="small"
              onClick={() => void roster.revoke(target.kind, target.ref, g.userId)}
              sx={{ cursor: "pointer" }}
            />
          ))}
        </Box>
      )}
      {roster.addPersonOpen ? (
        <>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by name or email…"
            value={roster.addQuery}
            onChange={(e) => roster.setAddQuery(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {filteredUsers.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
                No matching accounts.
              </Typography>
            ) : (
              filteredUsers.map((u: AdminUser) => (
                <Button
                  key={u.id}
                  size="small"
                  variant="outlined"
                  color="secondary"
                  disabled={busy}
                  onClick={() => void roster.addPerson(target.kind, target.ref, u.id)}
                >
                  {u.email}
                </Button>
              ))
            )}
          </Box>
        </>
      ) : (
        <Button size="small" variant="outlined" onClick={() => roster.setAddPersonOpen(true)}>
          ＋ Add person
        </Button>
      )}
    </Box>
  );
}

/** A flat row (Search tab) — tap opens the roster panel below it, no expand/drill affordance. */
function FlatAccessRow({ target, roster }: { target: GrantTarget; roster: AccessRoster }) {
  const key = `${target.kind}:${target.ref}`;
  const list = roster.rosters[key];
  const isOpen = roster.open === key;
  return (
    <Box sx={{ borderBottom: "1px solid", borderColor: "divider", "&:last-of-type": { borderBottom: "none" } }}>
      <ListItemButton onClick={() => roster.toggle(target.kind, target.ref)}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
          <Box>
            <Typography sx={{ fontWeight: 600 }}>{target.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {target.kind} · {target.path}
              {list ? ` · ${list.length} with access` : ""}
            </Typography>
          </Box>
          <Chip label={isOpen ? "▲" : "▼"} size="small" />
        </Box>
      </ListItemButton>
      {isOpen ? <AccessRosterPanel roster={roster} target={target} /> : null}
    </Box>
  );
}

/**
 * One row of the Browse tab's lazy drill-down tree — a catalog OR a pack. A catalog row gets a
 * SEPARATE "▸ show sub-items" expand toggle (independent of the roster toggle, per docs/365
 * item 2: "item 2 changes what's listed at rest, not the roster interaction") that, once
 * expanded, reveals its own `node.children`/`node.packs` **one level** at a time via
 * {@link catalogChildTargets} — no fetch, the whole tree is already loaded in memory — and
 * recurses into itself for a nested child catalog. A pack row is always a leaf (no expand).
 */
function CatalogTreeRow({
  node,
  target,
  depth,
  baseUrl,
  roster,
}: {
  node: CatalogNode;
  target: GrantTarget;
  depth: number;
  baseUrl: string;
  roster: AccessRoster;
}) {
  const [expanded, setExpanded] = useState(false);
  const key = `${target.kind}:${target.ref}`;
  const list = roster.rosters[key];
  const isRosterOpen = roster.open === key;
  const children = expanded ? catalogChildTargets(node, baseUrl) : null;
  const hasAnyChildren = node.children.length > 0 || node.packs.length > 0;

  return (
    <Box sx={{ borderBottom: depth === 0 ? "1px solid" : "none", borderColor: "divider" }}>
      <Box sx={{ display: "flex", alignItems: "stretch" }}>
        {hasAnyChildren ? (
          <IconButton
            size="small"
            onClick={() => setExpanded((e) => !e)}
            aria-label={expanded ? "Collapse" : "Expand"}
            sx={{ ml: depth * 2 + 0.5, alignSelf: "center" }}
          >
            {expanded ? "▾" : "▸"}
          </IconButton>
        ) : (
          <Box sx={{ width: 40, ml: depth * 2 }} />
        )}
        <ListItemButton onClick={() => roster.toggle(target.kind, target.ref)} sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>{target.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {target.kind} · {target.path}
                {list ? ` · ${list.length} with access` : ""}
              </Typography>
            </Box>
            <Chip label={isRosterOpen ? "▲" : "▼"} size="small" />
          </Box>
        </ListItemButton>
      </Box>
      {isRosterOpen ? <AccessRosterPanel roster={roster} target={target} /> : null}
      {children && (children.catalogs.length > 0 || children.packs.length > 0) ? (
        <Box>
          {children.catalogs.map((c) => (
            <CatalogTreeRow key={c.target.ref} node={c.node} target={c.target} depth={depth + 1} baseUrl={baseUrl} roster={roster} />
          ))}
          {children.packs.map((p) => (
            <Box key={p.ref} sx={{ display: "flex", alignItems: "stretch" }}>
              <Box sx={{ width: 40, ml: (depth + 1) * 2 }} />
              <FlatAccessRow target={p} roster={roster} />
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * Catalogs (catalog access management) tab, re-skinned to MUI/Mantis
 * (docs/337-ADMIN-MANTIS-MIGRATION.md v2) — mirrors `apps/admin-web/src/pages/CatalogsPage.tsx`
 * (itself mirroring `apps/admin/app/(tabs)/catalogs.tsx` + `ui/CatalogAccessRoster.tsx` /
 * `specs/admin/screens/catalogs.md`): for every grantable target (pack or catalog) in the loaded
 * catalog tree, see who has access and add/revoke a person. Every `useQuery` and mutation call
 * here is lifted UNCHANGED from `apps/admin-web`'s own page — only the returned JSX differs.
 *
 * **v1 simplification vs. the RN admin, carried over from `apps/admin-web`:** no protection-level
 * badge prefetch and no time-boxed/expiring-grant controls (docs/307 v1) — grants here are always
 * permanent. The underlying `adminAddGrant`/`adminRevokeGrant`/`adminGrantsByRef` calls are
 * unchanged, so upgrading to expiring grants later needs no data-model change, only more UI.
 *
 * **v2 (docs/365-ADMIN-USER-COMPLETION-AND-CATALOGS-TABS.md item 2/3):** replaced the flat,
 * arbitrarily-capped whole-tree list with a `TabbedPage` split into **Browse** (root catalogs
 * only, via `rootCatalogNodes` — the top-level unwrap the invite picker already uses — with a
 * lazy per-level drill-down via `catalogChildTargets`, no fetch) and **Search** (the original
 * whole-tree `grantTargets` flat filter, capped at `MAX_SEARCH_RESULTS`, for finding a deeply
 * nested pack by name). The roster (who-has-access) interaction is UNCHANGED and shared by both
 * tabs via {@link useAccessRoster} — item 2 changes what's listed at rest, not the roster.
 */
export function CatalogsPage() {
  const { client, token, backendUrl } = useAuth();
  const contentBaseUrl = publicContentBaseUrl(backendUrl);
  const { tree, loading: treeLoading, error: treeError } = useCatalogTree(backendUrl);
  const roots = useMemo(() => rootCatalogNodes(tree, contentBaseUrl), [tree, contentBaseUrl]);
  const allTargets = useMemo(() => grantTargets(tree, contentBaseUrl), [tree, contentBaseUrl]);

  const [query, setQuery] = useState("");
  const roster = useAccessRoster(client, token);

  const ql = query.trim().toLowerCase();
  const searchResults = ql
    ? allTargets.filter((tg) => `${tg.title} ${tg.ref} ${tg.path}`.toLowerCase().includes(ql)).slice(0, MAX_SEARCH_RESULTS)
    : [];

  const alerts = (
    <>
      {roster.usersQuery.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(roster.usersQuery.error as Error).message}
        </Alert>
      ) : null}
      {roster.rosterError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {roster.rosterError}
        </Alert>
      ) : null}
      {/* docs/340-ADMIN-MANTIS-AND-API-IDEAS.md §3 — `useCatalogTree` used to swallow a
          fetch failure into an empty tree, so this screen showed the "Loading…" spinner
          forever with no way to tell a real failure from a slow/empty load. Now the hook
          reports the three states separately: */}
      {treeError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Couldn&rsquo;t load the catalog tree: {treeError}
        </Alert>
      ) : null}
    </>
  );

  return (
    <MainCard title="Who has access">
      {alerts}
      {treeLoading ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 0 }}>
            Loading the catalog tree…
          </Typography>
        </Box>
      ) : allTargets.length === 0 ? (
        <Typography color="text.secondary" sx={{ mb: 0 }}>
          {treeError ? "No catalog data to show." : "No catalogs or packs found."}
        </Typography>
      ) : (
        <TabbedPage
          defaultTab="browse"
          tabs={[
            {
              id: "browse",
              label: "Browse",
              content:
                roots.length === 0 ? (
                  <Typography color="text.secondary" sx={{ mb: 0 }}>
                    No root catalogs found.
                  </Typography>
                ) : (
                  <List sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 0 }}>
                    {roots.map((r) => (
                      <CatalogTreeRow key={r.target.ref} node={r.node} target={r.target} depth={0} baseUrl={contentBaseUrl} roster={roster} />
                    ))}
                  </List>
                ),
            },
            {
              id: "search",
              label: "Search",
              content: (
                <>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search catalogs/packs…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  {!ql ? (
                    <Typography color="text.secondary" sx={{ mb: 0 }}>
                      Type to search the whole catalog tree by title, id, or path.
                    </Typography>
                  ) : searchResults.length === 0 ? (
                    <Typography color="text.secondary" sx={{ mb: 0 }}>
                      No matches.
                    </Typography>
                  ) : (
                    <List sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 0 }}>
                      {searchResults.map((tg) => (
                        <FlatAccessRow key={`${tg.kind}:${tg.ref}`} target={tg} roster={roster} />
                      ))}
                    </List>
                  )}
                </>
              ),
            },
          ]}
        />
      )}
    </MainCard>
  );
}
